/**
 * PublicLayout - site comercial (light)
 */
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LOGO_URL } from "@/lib/brand";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePublicSettings } from "@/lib/publicSettings";

const NAV = [
    { to: "/funcionalidades", label: "Funcionalidades" },
    { to: "/precos", label: "Preços" },
    { to: "/sobre", label: "Sobre" },
    { to: "/contacto", label: "Contacto" },
];

export default function PublicLayout() {
    const { user } = useAuth();
    const settings = usePublicSettings();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const isLanding = location.pathname === "/";
    const company = settings?.company || {};
    const contacts = settings?.contacts || {};

    return (
        <div className="min-h-screen bg-background text-foreground antialiased" data-testid="public-layout">
            {/* Top nav */}
            <header
                className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border"
                data-testid="public-header"
            >
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2" data-testid="public-logo-link">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0B0B10] overflow-hidden">
                            <img src={LOGO_URL} alt="MeSafa" className="h-9 w-9 object-contain" />
                        </span>
                        <span className="font-display font-bold text-lg tracking-tight">
                            <span className="text-[#6B21D9]">me</span>
                            <span>Safa</span>
                            <span className="text-muted-foreground text-xs ml-1 align-top">.ao</span>
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1" aria-label="Principal">
                        {NAV.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-lg text-sm font-medium ${
                                        isActive
                                            ? "text-foreground bg-accent"
                                            : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                                    }`
                                }
                                data-testid={`nav-${it.to.replace("/", "")}-link`}
                            >
                                {it.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        {user ? (
                            <Button asChild data-testid="go-app-button">
                                <Link to={user.role === "admin" ? "/admin" : "/painel"}>Entrar na app</Link>
                            </Button>
                        ) : (
                            <>
                                <Button asChild variant="ghost" data-testid="public-login-link">
                                    <Link to="/entrar">Entrar</Link>
                                </Button>
                                <Button
                                    asChild
                                    className="btn-grad-purple text-white hover:opacity-95"
                                    data-testid="public-cta-register-button"
                                >
                                    <Link to="/registo">Experimentar grátis</Link>
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Mobile */}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild className="md:hidden">
                            <Button variant="ghost" size="icon" data-testid="public-mobile-menu-button">
                                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[300px]">
                            <div className="flex flex-col gap-1 mt-4">
                                {NAV.map((it) => (
                                    <NavLink
                                        key={it.to}
                                        to={it.to}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `px-3 py-3 rounded-lg text-base font-medium ${
                                                isActive
                                                    ? "text-foreground bg-accent"
                                                    : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                                            }`
                                        }
                                    >
                                        {it.label}
                                    </NavLink>
                                ))}
                                <div className="mt-4 flex flex-col gap-2">
                                    {user ? (
                                        <Button asChild>
                                            <Link to={user.role === "admin" ? "/admin" : "/painel"}>
                                                Entrar na app
                                            </Link>
                                        </Button>
                                    ) : (
                                        <>
                                            <Button asChild variant="outline">
                                                <Link to="/entrar">Entrar</Link>
                                            </Button>
                                            <Button asChild className="btn-grad-purple text-white">
                                                <Link to="/registo">Experimentar grátis</Link>
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </header>

            {/* Outlet com contexto sobre se é landing */}
            <main data-is-landing={isLanding ? "true" : "false"}>
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="border-t border-border mt-16" data-testid="public-footer">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12 grid gap-8 md:grid-cols-2 lg:grid-cols-5">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0B0B10] overflow-hidden">
                                <img src={LOGO_URL} alt="MeSafa" className="h-9 w-9 object-contain" />
                            </span>
                            <span className="font-display font-bold text-lg">
                                <span className="text-[#6B21D9]">me</span>Safa
                                <span className="text-muted-foreground text-xs ml-1">.ao</span>
                            </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            O piloto automático das suas redes sociais.
                            <br />Feito em Angola, para África.
                        </p>
                        {company.address && (
                            <p className="text-xs text-muted-foreground mt-3" data-testid="footer-address">
                                {company.address}
                                <br />{company.city}, {company.country}
                            </p>
                        )}
                    </div>
                    <div>
                        <h4 className="font-semibold mb-3">Produto</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/funcionalidades" className="hover:text-foreground">Funcionalidades</Link></li>
                            <li><Link to="/precos" className="hover:text-foreground">Preços</Link></li>
                            <li><Link to="/registo" className="hover:text-foreground">Começar grátis</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold mb-3">Empresa</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/sobre" className="hover:text-foreground">Sobre nós</Link></li>
                            <li><Link to="/contacto" className="hover:text-foreground">Contacto</Link></li>
                            <li>
                                <a
                                    href="https://muds.ao/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-foreground"
                                    data-testid="footer-muds-link"
                                >
                                    MUDS Group
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold mb-3">Legal</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground" data-testid="footer-legal">
                            <li><Link to="/termos" className="hover:text-foreground">Termos e Condições</Link></li>
                            <li><Link to="/privacidade" className="hover:text-foreground">Privacidade</Link></li>
                            <li><Link to="/sla" className="hover:text-foreground">SLA</Link></li>
                            <li><Link to="/reembolso" className="hover:text-foreground">Reembolso</Link></li>
                            <li><Link to="/aviso-legal" className="hover:text-foreground">Aviso Legal</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold mb-3">Contactos</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground" data-testid="footer-contacts">
                            {contacts.general_email && <li>Geral: <a href={`mailto:${contacts.general_email}`} className="hover:text-foreground">{contacts.general_email}</a></li>}
                            {contacts.support_email && <li>Suporte: <a href={`mailto:${contacts.support_email}`} className="hover:text-foreground">{contacts.support_email}</a></li>}
                            {contacts.business_email && <li>Negócios: <a href={`mailto:${contacts.business_email}`} className="hover:text-foreground">{contacts.business_email}</a></li>}
                            {contacts.general_phone && <li>Tel: {contacts.general_phone}</li>}
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border">
                    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6 text-xs text-muted-foreground flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <span>
                            © {new Date().getFullYear()} {company.legal_name || "MUDS - Prestação de Serviços e Comércio Geral (SU), Lda"}. Todos os direitos reservados.{" "}
                            <a href="https://muds.ao/" target="_blank" rel="noopener noreferrer" className="text-[#6B21D9] hover:underline ml-1" data-testid="footer-copyright-muds">
                                muds.ao
                            </a>
                        </span>
                        <span>{company.city || "Luanda"} · {company.country || "Angola"}</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
